import { useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";

import Page from "components/Page/Page";
import MaxWidthPage from "components/MaxWidthPage";
import Tabs, { Tab } from "components/Tab";

import ScrollToHeaderTitle from "misc/ScrollToHeaderTitle";

import { useToasterFromActionData } from "hooks/useToasterFromActionData";
import { useRedirectFromActionData } from "hooks/useRedirectFromActionData";

import { BillingFlowProvider } from "./providers/BillingFlowProvider";

import {
  trackBillingV2PageViewed,
  trackBillingV2PageLoaded,
  trackBillingV2TabViewed,
} from "utils/Tracking";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { BillingTabsRoutes } from "v2/pages/billing/tabs/BillingTabsRoutes";
import { BillingPath } from "v2/pages/billing/BillingPath";


const TAB_PATH_TO_EVENT: Record<string, 'subscription' | 'billing_details' | 'manage_plan'> = {
  subscription: 'subscription',
  details: 'billing_details',
  manage: 'manage_plan',
};


export default function Billing() {
  return (
    <BillingFlowProvider>
      <BillingContent />
    </BillingFlowProvider>
  );
}

function BillingContent() {
  const { activeSubscription } = useActiveSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  // display toasters after actions
  useToasterFromActionData();

  // handle redirects from actions (e.g., missing payment method → billing details)
  useRedirectFromActionData();

  // Empty payload: subscription data resolves asynchronously, so per-event plan
  // segmentation comes from the user-level Product Fruits identify call instead.
  useEffect(() => {
    trackBillingV2PageViewed();
  }, []);

  const mountTimeRef = useRef(performance.now());
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current || !activeSubscription) return;
    loadedRef.current = true;
    trackBillingV2PageLoaded({
      load_time_ms: Math.round(performance.now() - mountTimeRef.current),
    });
  }, [activeSubscription]);

  const tabsPaths = Object.keys(BillingTabsRoutes);
  const selectedTabIdx = tabsPaths.findIndex(tabPath => location.pathname.startsWith(BillingPath + "/" + tabPath));

  const lastTrackedTabRef = useRef<string | null>(null);
  const currentTabPath = tabsPaths[selectedTabIdx];
  useEffect(() => {
    if (!currentTabPath) return;
    if (lastTrackedTabRef.current === currentTabPath) return;
    const eventTab = TAB_PATH_TO_EVENT[currentTabPath];
    if (eventTab) {
      trackBillingV2TabViewed(eventTab);
      lastTrackedTabRef.current = currentTabPath;
    }
  }, [currentTabPath]);

  const handleTabChange = (index: number) => {
    if (index === selectedTabIdx) return;
    const tabPath = tabsPaths[index];
    navigate(BillingPath + "/" + tabPath);
  };

  return (
    <MaxWidthPage>
      <ScrollToHeaderTitle />
      <Page title="Billing" subtitle="Manage your plan, usage, and payment details" stickyTitle>
        <div className="sticky top-[120px] bg-white z-10 pb-5">
          <Tabs selectedIndex={ selectedTabIdx } onChange={ handleTabChange }>
            { Object.entries(BillingTabsRoutes).map(([ path, tabProperties ]) =>
              <Tab key={ path } title={ tabProperties.title } />
            ) }
          </Tabs>
        </div>

        <div className="pt-5">
          <Outlet />
        </div>
      </Page>
    </MaxWidthPage>
  );
}
